export type AuthorizedProjectionStatus =
  "loading" | "absent" | "ready" | "error";

/**
 * Whether the journey projection is still coming, genuinely absent, or here.
 *
 * The distinction that matters is between "no request will ever be made" and
 * "a request has not answered yet", because Home resolves a *different patient*
 * from each. Absent means a viewer with no patient to fetch for, and the right
 * Home for her is the explorer one. Loading means wait.
 *
 * A React Query query cannot tell them apart on its own, and the bug was
 * assuming it could. `useExperienceContext` is `enabled: !!patientId`, and
 * `patientId` is read off the Stytch user's trusted metadata, which hydrates
 * asynchronously on web. So for the first renders after a page load the query is
 * *disabled*: TanStack reports `status: "pending"` with `fetchStatus: "idle"`,
 * which makes `isLoading` false and `data` undefined — indistinguishable, by
 * those two fields, from a settled empty answer. The projection therefore said
 * "absent", Home concluded the viewer had never started a cycle, and a patient
 * who had just finished one watched the first-time explorer Home for six renders
 * before it flipped to her real one.
 *
 * So the answer needs two facts the query does not carry: whether the session
 * has hydrated at all, and whether there is a patient the query will fetch for.
 */
export function resolveAuthorizedProjectionStatus(args: {
  /** `useIsAuthReady()` — false while the web SDK is still restoring a session. */
  isAuthReady: boolean;
  /** Whether a patient id is known, i.e. whether the query can run at all. */
  hasPatient: boolean;
  /** Whether the query has finished, either way. */
  isSettled: boolean;
  /**
   * Whether the query is paused rather than in flight — `fetchStatus:
   * "paused"`, which is what TanStack reports for an enabled query on an
   * offline client under the default `networkMode: "online"`.
   *
   * A paused query never settles: neither `isSuccess` nor `isError` becomes
   * true while connectivity is down. Counting it as "still coming" held the
   * whole screen on its loading state for as long as the device was offline —
   * worse than what it replaced, since the `isLoading` this presenter took over
   * from is `isPending && isFetching` and is therefore false when paused, so
   * Home used to render degraded but usable. Waiting is only right when an
   * answer is actually on its way.
   */
  isPaused?: boolean;
  hasError: boolean;
  hasData: boolean;
}): AuthorizedProjectionStatus {
  const { isAuthReady, hasPatient, isSettled, hasError, hasData, isPaused } =
    args;

  // Nothing is known yet, not even whether there is a viewer.
  if (!isAuthReady) return "loading";

  // There is a patient, so an answer is coming — including the frame in which
  // the query has just been enabled and has not started fetching. A paused
  // query is the exception: nothing is on its way until the network returns.
  if (hasPatient && !isSettled && !isPaused) return "loading";

  if (hasError) return "error";

  // Settled, or no patient to settle for: an absent projection is now a fact
  // about the viewer rather than about the clock.
  if (!hasData) return "absent";

  return "ready";
}
